import { HttpStatusError, readSse } from "./sse.ts";

/**
 * Client de l'API chatbot (ai.mysmartbook.fr), mêmes routes que l'app mobile
 * (DefaultDiscussionNetworkDataSource.kt, ChatSse.kt).
 *
 * Authentification : jeton « reader-application » (JWT HS256, `sub` = identifiant client
 * Shopware) émis par le site, envoyé en `Authorization: Bearer` avec
 * `X-Auth-Provider: reader-application`.
 */
import type { QuoteSource } from "./quoteLinks.ts";
import type { AreaPlace } from "./aroundMe.ts";
import type { Quiz, QuizAnswer, QuizList, QuizOptions, QuizPosition, QuizScope } from "./quiz.ts";

export type Role = "user" | "assistant";

export interface RemoteMessage {
  id: string;
  conversation_id: string;
  content: string;
  role: Role;
  rating: number | null;
  rating_message: string | null;
  created_at: string;
  updated_at: string | null;
  /** Passages du livre cités par la réponse, avec leur Locator. */
  metadata?: { quotes?: QuoteSource[] | null } | null;
}

/** Une personne du livre, telle que l'API la donne pour « Discuter avec… ». */
export interface BookPerson {
  name: string;
  kind?: string | null;
  gender?: string | null;
  description?: string | null;
  /** Fichier du livre où elle apparaît pour la première fois : pas proposée avant, pour ne rien dévoiler. */
  first_href?: string | null;
  first_section?: string | null;
}

export interface RemoteConversation {
  id: string;
  name?: string | null;
  /** null : la conversation du livre ; "character" : un dialogue avec une personne du livre. */
  kind?: string | null;
  character?: { name: string; kind?: string; description?: string } | null;
  /** La langue de la conversation, choisie à sa première question puis fixe ; aucune pour celles commencées avant. */
  language?: string | null;
  product_ids: string[];
  created_at: string;
  updated_at: string | null;
}

/** Ce que le back-office permet pour un livre dans l'application (« Fonctionnalités et conditions »). */
export interface ProductFeatures {
  features: Record<string, boolean>;
  settings: {
    /** Questions de départ choisies pour la catégorie du livre (clés des textes proposés, ou questions écrites). */
    questions?: string[] | null;
    /** Le récap est proposé à partir de ce chapitre. */
    summary_min_chapter?: number | null;
    max_persons?: number | null;
  };
  /** Catégorie du catalogue (premier niveau), en français. */
  category?: string | null;
}

export type ChatSseEvent =
  | { type: "connected" }
  | { type: "idle" }
  | { type: "processing"; content: string }
  | { type: "answerPiece"; content: string }
  | { type: "dbUpdated"; newMessages: RemoteMessage[]; updatedMessages: RemoteMessage[] };

export class ChatApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ChatApiError";
  }

  /** Jeton absent, expiré ou refusé : il faut rouvrir le livre depuis le site. */
  get isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

export interface ChatApiOptions {
  baseUrl: string;
  token: string;
  fetchImpl?: typeof fetch;
}

export class ChatApi {
  private readonly baseUrl: string;
  private readonly token: string;
  private readonly fetchImpl: typeof fetch;

  constructor(options: ChatApiOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.token = options.token;
    this.fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
  }

  private get headers(): Record<string, string> {
    return {
      Authorization: `Bearer ${this.token}`,
      "X-Auth-Provider": "reader-application",
    };
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method,
      headers: body === undefined ? this.headers : { ...this.headers, "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!response.ok) {
      throw new ChatApiError(response.status, await errorDetail(response));
    }
    return (await response.json()) as T;
  }

  findOrCreateConversation(productId: string): Promise<RemoteConversation> {
    return this.request("POST", `/server/chat/conversation/product-id/${encodeURIComponent(productId)}/find-or-create`);
  }

  /** Toutes les conversations du lecteur (tous livres, toutes sortes). */
  listConversations(): Promise<RemoteConversation[]> {
    return this.request("GET", "/server/chat/conversation/all");
  }

  /** Les personnes du livre (vide sans extraction), pour « Discuter avec… ». */
  getCharacters(productId: string): Promise<BookPerson[]> {
    return this.request("GET", `/server/chat/conversation/product-id/${encodeURIComponent(productId)}/characters`);
  }

  /** Ouvre un dialogue avec une personne du livre ; le serveur y met sa première réplique. */
  createCharacterConversation(productId: string, person: BookPerson): Promise<RemoteConversation> {
    return this.request("POST", "/server/chat/conversation", {
      product_ids: [productId],
      kind: "character",
      name: person.name,
      character: { name: person.name, kind: person.kind || "personnage", description: person.description ?? "" },
    });
  }

  getMessages(conversationId: string): Promise<RemoteMessage[]> {
    return this.request("GET", `/server/chat/conversation/${encodeURIComponent(conversationId)}/messages`);
  }

  /**
   * Envoie une question. La requête ne se termine qu'une fois la réponse générée ;
   * la réponse arrive en direct par le flux SSE.
   */
  sendMessage(conversationId: string, message: string, language?: string | null): Promise<RemoteMessage> {
    // `language` : celle choisie pour la conversation, gardée par le serveur à sa première question (puis fixe).
    return this.request("POST", "/server/chat", { message, conversation_id: conversationId, ...(language ? { language } : {}) });
  }

  /** Fonctionnalités du livre décidées au back-office pour ChatNovel (récap, questions de départ…), comme Studio. */
  getProductFeatures(productId: string): Promise<ProductFeatures> {
    return this.request("GET", `/server/book/product-id/${encodeURIComponent(productId)}/features?app=chatnovel`);
  }

  /** La zone couverte par un guide de voyage, pour « Autour de moi » (`area` null : inconnue). */
  getGuideArea(productId: string): Promise<{ area: { places: AreaPlace[] } | null; margin_km: number }> {
    return this.request("GET", `/server/book/product-id/${encodeURIComponent(productId)}/around-me/area`);
  }

  /** Les portées de quiz proposées là où est le lecteur, et ce qu'il reste pour aujourd'hui. */
  quizOptions(productId: string, href: string): Promise<QuizOptions> {
    const query = new URLSearchParams({ product_id: productId, current_href: href });
    return this.request("GET", `/server/quiz/options?${query}`);
  }

  /** Les quiz du lecteur sur le livre, et le score du livre. */
  bookQuizzes(productId: string): Promise<QuizList> {
    return this.request("GET", `/server/quiz?${new URLSearchParams({ product_id: productId })}`);
  }

  /**
   * Un nouveau quiz sur une portée, ou celui commencé dessus (repris). `language` : l'écrire dans la langue de
   * l'interface plutôt que celle du livre (les drapeaux du chat, Stéphane 19/10/2026).
   */
  createQuiz(productId: string, scope: QuizScope, position: QuizPosition, language?: string | null): Promise<Quiz> {
    return this.request("POST", "/server/quiz", {
      product_id: productId,
      scope,
      current_href: position.href,
      reading_order: position.readingOrder,
      // La section que voit le lecteur, pour un quiz sur elle (sinon celle de la table des matières).
      section_label: scope === "current" ? position.section || undefined : undefined,
      language: language || undefined,
    });
  }

  /** Un quiz, pour le reprendre ou le relire. */
  getQuiz(quizId: string): Promise<Quiz> {
    return this.request("GET", `/server/quiz/${encodeURIComponent(quizId)}`);
  }

  /** « Annuler le quiz », ou sa poubelle : il n'apparaît plus et ne compte plus (mais reste compté dans la journée). */
  deleteQuiz(quizId: string): Promise<{ deleted: boolean }> {
    return this.request("DELETE", `/server/quiz/${encodeURIComponent(quizId)}`);
  }

  /** Un essai sur une question. */
  answerQuiz(quizId: string, question: number, choice: number): Promise<QuizAnswer> {
    return this.request("POST", `/server/quiz/${encodeURIComponent(quizId)}/answer`, { question, choice });
  }

  /** `isGood` : true = 1, false = 0, null = retirer la note. */
  rateMessage(messageId: string, isGood: boolean | null, message: string | null = null): Promise<RemoteMessage> {
    return this.request("PATCH", `/server/chat/message/${encodeURIComponent(messageId)}/rating`, {
      rating: isGood === null ? null : isGood ? 1 : 0,
      rating_message: message,
    });
  }

  /** Flux des réponses. Se termine à la déconnexion ; lève `ChatApiError` si l'accès est refusé. */
  async stream(conversationId: string, signal: AbortSignal, onEvent: (event: ChatSseEvent) => void): Promise<void> {
    try {
      await readSse(
        this.fetchImpl,
        `${this.baseUrl}/server/chat/${encodeURIComponent(conversationId)}/sse`,
        this.headers,
        signal,
        (message) => {
          const event = toChatEvent(message.event, message.data);
          if (event) onEvent(event);
        },
      );
    } catch (e) {
      if (e instanceof HttpStatusError) throw new ChatApiError(e.status, `Flux indisponible (HTTP ${e.status})`);
      throw e;
    }
  }
}

export function toChatEvent(event: string, data: string): ChatSseEvent | undefined {
  try {
    switch (event) {
      case "init":
        return { type: "connected" };
      case "idle":
        return { type: "idle" };
      case "processing":
        return { type: "processing", content: String(JSON.parse(data).content ?? "") };
      case "streaming":
        return { type: "answerPiece", content: String(JSON.parse(data).content ?? "") };
      case "db_update": {
        const parsed = JSON.parse(data);
        return {
          type: "dbUpdated",
          newMessages: parsed.new_messages ?? [],
          updatedMessages: parsed.updated_messages ?? [],
        };
      }
      default:
        return undefined;
    }
  } catch {
    return undefined;
  }
}

async function errorDetail(response: Response): Promise<string> {
  try {
    const body = await response.json();
    if (typeof body?.detail === "string") return body.detail;
  } catch {
    // corps non JSON
  }
  return `HTTP ${response.status}`;
}
